import React from 'react';
import fuzzy from 'fuzzy';
import { defineMessages, useIntl } from 'react-intl';
import { UIIcon } from 'dig-components/icons';
import { SearchLine } from 'dig-components/icons/src';
import { TextInput } from 'dig-components/text_fields';
import { Typeahead } from 'dig-components/typeahead';
import { Text, TextProps } from 'dig-components/typography';

import { GenericObject } from 'common/types/generic-object';

function getScopedEntities(entities: GenericObject[]): GenericObject[] {
  return entities.filter((entity) => !entity.isSelf);
}

function fuzzySearchEntityNames(query: string, entities: GenericObject[]): GenericObject[] {
  const entitiesWithNames = entities.filter((entity) => !!entity.name);

  return fuzzy.filter(query, entitiesWithNames, {
    pre: '<span class="team-access-modal__entity-access-list-item-fuzzy">',
    post: '</span>',
    extract(entity) {
      return entity.name;
    },
  });
}

function fuzzySearchEntityEmails(query: string, entities: GenericObject[]) {
  const entitiesWithEmails = entities.filter((entity) => !!entity.email);

  return fuzzy.filter(query, entitiesWithEmails, {
    pre: '<span class="team-access-modal__entity-access-list-item-fuzzy">',
    post: '</span>',
    extract(entity) {
      return entity.email;
    },
  });
}

function dedupeSearchResultsByGuid(resultsWithKey: { results: GenericObject[], key: string }[]) {
  const dedupedResults = [];
  const dirtyGuids = new Set();
  const resultsByGuid: GenericObject = {};

  for (let i = 0, len = resultsWithKey.length; i < len; i++) {
    const { results, key } = resultsWithKey[i];

    for (let j = 0, lenJ = results.length; j < lenJ; j++) {
      const result = results[j];
      const entity = result.original;
      if (entity.guid in resultsByGuid) {
        resultsByGuid[entity.guid][key] = result.string;
      } else {
        resultsByGuid[entity.guid] = {
          [key]: result.string,
        };
      }
    }
  }

  const resultsToDedupe = resultsWithKey.map(({ results }) => results).flat();

  for (let i = 0, len = resultsToDedupe.length; i < len; i++) {
    const result = resultsToDedupe[i];
    const entity = result.original;

    if (!dirtyGuids.has(entity.guid)) {
      dirtyGuids.add(entity.guid);
      dedupedResults.push({
        ...result,
        matches: resultsByGuid[entity.guid],
      });
    }
  }

  return dedupedResults;
}

function fuzzySearchEntities(query: string, entities: GenericObject[]) {
  return dedupeSearchResultsByGuid(
    [
      {
        results: fuzzySearchEntityNames(query, entities),
        key: 'name',
      },
      {
        results: fuzzySearchEntityEmails(query, entities),
        key: 'email',
      },
    ],
  );
}

const messages = defineMessages({
  searchByEmail: {
    id: '',
    description: 'placeholder text indicating to search by email or team name in team access modal in doc page',
    defaultMessage: 'Search by name, email, or team',
  },
  alreadyAdded: {
    id: '',
    description: 'Already added',
    defaultMessage: 'Already added',
  },
  noMatches: {
    id: '',
    description: 'No matching names, emails, or teams',
    defaultMessage: 'No matching names, emails, or teams',
  },
});

interface Props {
  entities: GenericObject[];
  grantAccess: Function;
}

export function EntityTypeahead({ entities, grantAccess }: Props) {
  const intl = useIntl();
  const entitiesInScope = React.useMemo(() => getScopedEntities(entities), [entities]);

  const [textValue, setTextValue] = React.useState('');
  const [entityResults, setEntityResults] = React.useState<GenericObject[]>([]);

  const onTextChange = React.useCallback((e) => {
    setTextValue(e.target.value);
  }, [setTextValue]);

  React.useEffect(() => {
    const query = textValue;

    if (query.length) {
      const matchingEntities = fuzzySearchEntities(query, entitiesInScope);

      setEntityResults(matchingEntities);
    } else {
      setEntityResults([]);
    }
  }, [entitiesInScope, textValue, setEntityResults]);

  const alreadyAdded = (
    <Text
      color='disabled'
      size='small'
      variant='label'
    >{intl.formatMessage(messages.alreadyAdded)}</Text>
  );

  const renderTypeaheadRow = ({ matches, original }: GenericObject) => {
    const hasAccess = original.hasExplicitTemplateAccess
      || original.has_implicit_template_access
      || original.willAccessBeGranted;
    let titleProps: TextProps = {};
    let subtitleProps: TextProps | null = null;
    if (original.name) {
      if (matches.name) {
        titleProps = {
          dangerouslySetInnerHTML: { __html: matches.name },
        };
      } else {
        titleProps = {
          children: original.name,
        };
      }

      if (matches.email) {
        subtitleProps = {
          dangerouslySetInnerHTML: { __html: matches.email },
        };
      } else if (original.email) {
        subtitleProps = {
          children: original.email,
        };
      }
    } else {
      if (matches.email) { // eslint-disable-line no-lonely-if
        titleProps = {
          dangerouslySetInnerHTML: { __html: matches.email },
        };
      } else if (original.email) {
        titleProps = {
          children: original.email,
        };
      }
    }

    const title = <Text {...titleProps} />;
    const subtitle = subtitleProps ? <Text {...subtitleProps} /> : null;

    return (
      <Typeahead.Row
        key={original.guid}
        disabled={hasAccess}
        withTitle={title}
        withRightAccessory={hasAccess ? alreadyAdded : null}
        withSubtitle={subtitle}
        value={original} />
    );
  };
  const resetSearch = React.useCallback(() => {
    setTextValue('');
    setEntityResults([]);
  }, []);
  const grantEntityAccess = React.useCallback((entity) => {
    grantAccess(entity, true);
    resetSearch();
  }, [grantAccess, resetSearch]);

  return (
    <Typeahead.Wrapper onSelection={grantEntityAccess}>
      {({ getTriggerProps, getContentProps }) => (
        <>
          <TextInput
            {...getTriggerProps({ onChange: onTextChange })}
            className="team-access-modal__entity-typeahead-textinput"
            placeholder={intl.formatMessage(messages.searchByEmail)}
            aria-label={intl.formatMessage(messages.searchByEmail)}
            value={textValue}
            withLeftAccessory={<UIIcon src={SearchLine} />} />
          <Typeahead.Container
            {...getContentProps()}
            isEmptyQuery={!entityResults.length && !!textValue.length}
            emptyState={
              <Typeahead.EmptyState placeholderText={intl.formatMessage(messages.noMatches)} />
            }
          >
            <Typeahead.Results
              results={entityResults}
              renderRow={renderTypeaheadRow} />
          </Typeahead.Container>
        </>
      )}
    </Typeahead.Wrapper>
  );
}
